/**
 * Lynk Alert™ Ops (ADM-44, docs/admin-portal.md §17.7). LynkAlertAdminController's 5 kill
 * switches + 2 read-only metrics had no admin-portal UI — curl/Postman with a hand-obtained
 * bearer token was the only way to flip a Lynk Alert flag in production. This is the first UI.
 */
import { useState } from 'react';
import { apiGet, apiPatch, ApiError } from '../lib/apiClient';
import { useApiResource } from '../lib/useApiResource';
import { StatusBadge } from '../components/StatusBadge';
import { DataTable } from '../components/DataTable';
import { Switch } from '../components/FormCheck';

interface LynkAlertFlags {
  lynkAlertEnabled: boolean;
  rolloutPercent: number;
  voipEnabled: boolean;
  sentinelEnabled: boolean;
  ghostSkipEnabled: boolean;
}

interface AckLatencyHistogram {
  buckets: { label: string; count: number }[];
  totalSamples: number;
}

interface GhostSkipFpRate {
  falsePositiveRate: number;
  falsePositives: number;
  totalGhostSkips: number;
}

const TOGGLE_KEYS: {
  key: keyof Pick<LynkAlertFlags, 'lynkAlertEnabled' | 'voipEnabled' | 'sentinelEnabled' | 'ghostSkipEnabled'>;
  label: string;
  hint: string;
  endpoint: string;
}[] = [
  {
    key: 'lynkAlertEnabled',
    label: 'Lynk Alert',
    hint: 'Master switch. Off disables the entire alert pipeline for every user.',
    endpoint: 'flags/lynk-alert-enabled',
  },
  {
    key: 'voipEnabled',
    label: 'VoIP',
    hint: 'Voice calls on alert escalation. Off falls back to push only.',
    endpoint: 'flags/lynk-alert-voip-enabled',
  },
  {
    key: 'sentinelEnabled',
    label: 'Sentinel',
    hint: 'Background monitoring that raises alerts without user action.',
    endpoint: 'flags/sentinel-enabled',
  },
  {
    key: 'ghostSkipEnabled',
    label: 'Ghost-skip',
    hint: 'Suppresses alerts Sentinel scores as false positives.',
    endpoint: 'flags/sentinel-ghost-skip-enabled',
  },
];

function FlagsTab() {
  const { data: flags, loading, error, reload } = useApiResource(
    () => apiGet<LynkAlertFlags>('/lynk-alert/admin/flags'),
    [],
  );
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [rolloutDraft, setRolloutDraft] = useState<string | null>(null);

  const toggle = async (endpoint: string, key: string, current: boolean) => {
    setBusyKey(key);
    setActionError(null);
    try {
      await apiPatch(`/lynk-alert/admin/${endpoint}`, { enabled: !current });
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Toggle failed');
    } finally {
      setBusyKey(null);
    }
  };

  const saveRollout = async () => {
    if (rolloutDraft === null || flags === null) return;
    const n = Number(rolloutDraft);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      setActionError('Rollout percent must be 0-100.');
      return;
    }
    setBusyKey('rolloutPercent');
    setActionError(null);
    try {
      await apiPatch('/lynk-alert/admin/flags/lynk-alert-rollout-percent', { percent: n });
      setRolloutDraft(null);
      reload();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Rollout update failed');
    } finally {
      setBusyKey(null);
    }
  };

  const showLoadingState = !flags && loading;

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      {actionError && <div className="error-banner">{actionError}</div>}

      {showLoadingState ? (
        <div className="empty-state">Loading…</div>
      ) : !flags ? (
        <div className="empty-state">Could not load Lynk Alert flags.</div>
      ) : (
        <div className="form-card" style={{ maxWidth: 'none' }}>
          {/* One switch per flag, not a badge plus a button in a table. The switch reports
              the state and sets it, which is what a kill switch is. */}
          <div className="setting-list">
            {TOGGLE_KEYS.map(({ key, label, hint, endpoint }) => (
              <div className="setting-row" key={key}>
                <div className="setting-row-text">
                  <div className="setting-row-name">{label}</div>
                  <div className="setting-row-hint">{hint}</div>
                </div>
                <div className="setting-row-control">
                  <span className="setting-row-state">
                    {busyKey === key ? '…' : flags[key] ? 'On' : 'Off'}
                  </span>
                  <Switch
                    checked={flags[key]}
                    disabled={busyKey === key}
                    tone="success"
                    large
                    ariaLabel={label}
                    onChange={() => void toggle(endpoint, key, flags[key])}
                  />
                </div>
              </div>
            ))}

            <div className="setting-row">
              <div className="setting-row-text">
                <div className="setting-row-name">Rollout percent</div>
                <div className="setting-row-hint">
                  Share of eligible users the pipeline is live for. 0 is off for everyone, 100 is
                  everyone.
                </div>
              </div>
              <div className="setting-row-control">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  aria-label="Rollout percent"
                  value={rolloutDraft ?? flags.rolloutPercent.toString()}
                  onChange={(e) => setRolloutDraft(e.target.value)}
                  style={{ width: '5rem', height: 32, padding: '0 0.5rem', textAlign: 'right' }}
                />
                <button
                  className="btn btn-primary"
                  disabled={
                    busyKey === 'rolloutPercent' ||
                    rolloutDraft === null ||
                    rolloutDraft === flags.rolloutPercent.toString()
                  }
                  onClick={() => void saveRollout()}
                >
                  {busyKey === 'rolloutPercent' ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetricsTab() {
  const { data: ackLatency, loading: ackLoading, error: ackError } = useApiResource(
    () => apiGet<AckLatencyHistogram>('/lynk-alert/admin/metrics/ack-latency'),
    [],
  );
  const { data: ghostSkip, loading: ghostLoading, error: ghostError } = useApiResource(
    () => apiGet<GhostSkipFpRate>('/lynk-alert/admin/metrics/ghost-skip-fp-rate'),
    [],
  );

  return (
    <div className="card-grid">
      <div className="form-card">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Ack-latency histogram</h2>
        {ackError && <div className="error-banner">{ackError}</div>}
        {ackLoading && !ackLatency ? (
          <div className="empty-state">Loading…</div>
        ) : !ackLatency || ackLatency.buckets.length === 0 ? (
          <div className="empty-state">No ack-latency samples.</div>
        ) : (
          <>
            <p className="form-hint">{ackLatency.totalSamples} samples</p>
            <DataTable
              rows={ackLatency.buckets}
              getRowKey={(b) => b.label}
              controls={false}
              pageSize={null}
              columns={[
                { key: 'bucket', header: 'Bucket', value: (b) => b.label },
                { key: 'count', header: 'Count', value: (b) => b.count, align: 'right' },
              ]}
            />
          </>
        )}
      </div>

      <div className="form-card">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem' }}>Ghost-skip false-positive rate</h2>
        {ghostError && <div className="error-banner">{ghostError}</div>}
        {ghostLoading && !ghostSkip ? (
          <div className="empty-state">Loading…</div>
        ) : !ghostSkip ? (
          <div className="empty-state">No ghost-skip data.</div>
        ) : (
          <div style={{ background: 'var(--bg)', padding: '1rem', borderRadius: '3px' }}>
            <p><strong>False-positive rate:</strong> {(ghostSkip.falsePositiveRate * 100).toFixed(1)}%</p>
            <p><strong>False positives:</strong> {ghostSkip.falsePositives}</p>
            <p><strong>Total ghost-skips:</strong> {ghostSkip.totalGhostSkips}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export function LynkAlertOpsPage() {
  const [tab, setTab] = useState<'flags' | 'metrics'>('flags');

  return (
    <div>
      <div className="section-header">
        <div>
          <h1>Lynk Alert™ Ops</h1>
          <p>Kill switches and metrics for the Lynk Alert emergency-notification system. Changes take effect immediately.</p>
        </div>
      </div>

      <div className="form-card" style={{ maxWidth: 'none', marginBottom: '1rem', background: 'var(--surface-muted, var(--surface))' }}>
        <StatusBadge label="Live — affects real notifications" tone="warn" />
      </div>

      <div className="tabs">
        <button className={`tab-btn ${tab === 'flags' ? 'active' : ''}`} onClick={() => setTab('flags')}>
          Kill switches
        </button>
        <button className={`tab-btn ${tab === 'metrics' ? 'active' : ''}`} onClick={() => setTab('metrics')}>
          Metrics
        </button>
      </div>

      {tab === 'flags' ? <FlagsTab /> : <MetricsTab />}
    </div>
  );
}
